// src/components/resumo/PageManagerModal.tsx
import React, { useState } from 'react';
import { X, Trash2, ChevronUp, ChevronDown, Plus, BookOpen, Pencil, Check } from 'lucide-react';
import { ResumoPage } from './components/PageNavigator';
import './PageManagerModal.css';

interface PageManagerModalProps {
    pages: ResumoPage[];
    currentPageIndex: number;
    onDeletePage: (index: number) => void;
    onMovePage: (fromIndex: number, toIndex: number) => void;
    onInsertPageAfter: (afterIndex: number) => void;
    onAddPageAtEnd: () => void;
    onNavigateToPage: (index: number) => void;
    onRenamePage?: (index: number, newTitle: string) => void;
    onClose: () => void;
    darkMode?: boolean;
}

const PageManagerModal: React.FC<PageManagerModalProps> = ({
    pages,
    currentPageIndex,
    onDeletePage,
    onMovePage,
    onInsertPageAfter,
    onAddPageAtEnd,
    onNavigateToPage,
    onRenamePage,
    onClose,
    darkMode = false,
}) => {
    const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingTitle, setEditingTitle] = useState('');

    const commitRename = (index: number) => {
        const trimmed = editingTitle.trim();
        onRenamePage?.(index, trimmed || `Página ${index + 1}`);
        setEditingIndex(null);
    };

    return (
        <div className="pgm-overlay" onClick={onClose}>
            <div className={`pgm-modal${darkMode ? ' dark' : ''}`} onClick={e => e.stopPropagation()}>
                <div className="pgm-header">
                    <div className="pgm-header-info">
                        <BookOpen size={18} />
                        <h3>Gerenciar Apostila</h3>
                        <span className="pgm-page-count">{pages.length} página{pages.length !== 1 ? 's' : ''}</span>
                    </div>
                    <button className="pgm-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className="pgm-body">
                    {pages.map((page, index) => (
                        <React.Fragment key={page.id}>
                            <div className={`pgm-page-item${index === currentPageIndex ? ' active' : ''}`}>
                                <span className="pgm-page-num">{index + 1}</span>

                                {editingIndex === index ? (
                                    <input
                                        className="pgm-title-input"
                                        value={editingTitle}
                                        autoFocus
                                        onChange={e => setEditingTitle(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') commitRename(index);
                                            if (e.key === 'Escape') setEditingIndex(null);
                                        }}
                                        onBlur={() => commitRename(index)}
                                        onClick={e => e.stopPropagation()}
                                    />
                                ) : (
                                    <button
                                        className="pgm-page-title"
                                        onClick={() => { onNavigateToPage(index); onClose(); }}
                                        title="Ir para esta página"
                                    >
                                        {page.title || `Página ${index + 1}`}
                                        {index === currentPageIndex && <span className="pgm-current-badge">atual</span>}
                                    </button>
                                )}

                                <div className="pgm-page-actions">
                                    {onRenamePage && editingIndex !== index && (
                                        <button
                                            className="pgm-action-btn pgm-action-rename"
                                            onClick={() => { setEditingIndex(index); setEditingTitle(page.title || `Página ${index + 1}`); }}
                                            title="Renomear página"
                                        >
                                            <Pencil size={13} />
                                        </button>
                                    )}
                                    {editingIndex === index && (
                                        <button
                                            className="pgm-action-btn pgm-action-confirm"
                                            onClick={() => commitRename(index)}
                                            title="Confirmar nome"
                                        >
                                            <Check size={13} />
                                        </button>
                                    )}
                                    <button
                                        className="pgm-action-btn"
                                        onClick={() => onMovePage(index, index - 1)}
                                        disabled={index === 0}
                                        title="Mover para cima"
                                    >
                                        <ChevronUp size={14} />
                                    </button>
                                    <button
                                        className="pgm-action-btn"
                                        onClick={() => onMovePage(index, index + 1)}
                                        disabled={index === pages.length - 1}
                                        title="Mover para baixo"
                                    >
                                        <ChevronDown size={14} />
                                    </button>

                                    {confirmDeleteIndex === index ? (
                                        <div className="pgm-confirm-delete">
                                            <span>Excluir?</span>
                                            <button
                                                className="pgm-confirm-yes"
                                                onClick={() => { onDeletePage(index); setConfirmDeleteIndex(null); }}
                                            >
                                                Sim
                                            </button>
                                            <button
                                                className="pgm-confirm-no"
                                                onClick={() => setConfirmDeleteIndex(null)}
                                            >
                                                Não
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="pgm-action-btn pgm-action-delete"
                                            onClick={() => setConfirmDeleteIndex(index)}
                                            disabled={pages.length <= 1}
                                            title={pages.length <= 1 ? 'Não é possível excluir a única página' : 'Excluir página'}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Botão de inserir página entre as páginas */}
                            {index < pages.length - 1 && (
                                <button
                                    className="pgm-insert-between"
                                    onClick={() => onInsertPageAfter(index)}
                                    title={`Inserir página entre ${index + 1} e ${index + 2}`}
                                >
                                    <Plus size={12} />
                                    <span>inserir aqui</span>
                                </button>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <div className="pgm-footer">
                    <button className="pgm-btn-add-end" onClick={() => { onAddPageAtEnd(); onClose(); }}>
                        <Plus size={15} />
                        Adicionar página no final
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PageManagerModal;
